import {
  normalizeTeamCountry,
  teamCountryDialCode,
  type TeamCountryCode,
} from '@/constants/teamCountries';

const EXAMPLES: Record<TeamCountryCode, string> = {
  AU: '04XX XXX XXX or 02 XXXX XXXX',
  NZ: '021 XXX XXXX or 09 XXX XXXX',
  US: '202 555 0100',
  CA: '416 555 0100',
  UK: '07123 456789 or 020 7946 0000',
};

function digitsOnly(raw: string): string {
  return raw.replace(/\D/g, '');
}

/** National significant number, without country code or a single trunk 0. */
function toNationalSignificant(raw: string, country: TeamCountryCode): string | null {
  let digits = digitsOnly(raw);
  if (!digits) return null;

  if (digits.startsWith('00')) digits = digits.slice(2);

  const dial = digitsOnly(teamCountryDialCode(country));
  if (dial === '1') {
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  } else if (digits.startsWith(dial) && digits.length >= dial.length + 8) {
    digits = digits.slice(dial.length);
  }

  if (digits.startsWith('0')) digits = digits.slice(1);
  if (!digits || digits.startsWith('0')) return null;
  return digits;
}

function isValidNational(nsn: string, country: TeamCountryCode): boolean {
  switch (country) {
    case 'AU':
      return /^4\d{8}$/.test(nsn) || /^[2378]\d{8}$/.test(nsn) || /^1[38]00\d{6}$/.test(nsn);
    case 'NZ':
      return /^[2-9]\d{7,9}$/.test(nsn);
    case 'UK':
      return /^[1-9]\d{8,9}$/.test(nsn);
    case 'US':
    case 'CA':
      return /^[2-9]\d{2}[2-9]\d{6}$/.test(nsn);
    default:
      return false;
  }
}

export function isValidTeamPhone(raw: string, country?: string | null): boolean {
  const code = normalizeTeamCountry(country);
  const nsn = toNationalSignificant(raw, code);
  if (!nsn) return false;
  return isValidNational(nsn, code);
}

export function teamPhoneErrorMessage(country?: string | null): string {
  const code = normalizeTeamCountry(country);
  return `Enter a valid ${code} phone number (e.g. ${EXAMPLES[code]}).`;
}

/** Empty is allowed when the field is optional. */
export function validateTeamPhone(
  raw: string,
  country?: string | null,
  required = false,
): string | undefined {
  const value = raw.trim();
  if (!value) return required ? 'Phone number is required.' : undefined;
  if (!isValidTeamPhone(value, country)) return teamPhoneErrorMessage(country);
  return undefined;
}
